/**
 * The Installed Apps page's data: GET /apps/manifests returns one row per stored version of every app in the tenant;
 * this groups them per app key and works out what the page shows for each one, as pure functions over those rows.
 */
import { AppManifestSchema, describeDiff, diffManifests, summarizeManifest, type AppManifest, type ManifestSummary } from '@diggens/types/apps';

export type TenantAppStatus = 'draft' | 'active' | 'disabled';

/** A tenant_apps row as GET /apps/manifests returns it. */
export interface TenantAppRecord {
  id: string;
  tenant_id: string;
  key: string;
  version: number;
  status: TenantAppStatus;
  manifest: unknown;
  created_by: string | null;
  created_by_name: string | null;
  created_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface AppVersionView {
  id: string;
  version: number;
  status: TenantAppStatus;
  /** The parsed manifest, or null when the stored one no longer parses. */
  manifest: AppManifest | null;
  createdAt: string;
  updatedAt: string;
  /** Who stored this version: their name, else their email, else "Unknown". */
  createdBy: string;
  /** What this version added and removed against the previous version by number, one line each; empty when nothing changed by key. */
  changes: string[];
  /** True for the lowest version of the app, which has nothing to compare with. */
  isFirst: boolean;
}

export interface AppNavLink {
  path: string;
  label: string;
}

export interface InstalledAppView {
  key: string;
  name: string;
  description: string | undefined;
  /** The app's standing: one active version, or every version switched off, or drafts only. */
  status: TenantAppStatus;
  activeVersion: AppVersionView | null;
  /** The version the page describes: the active one, else the newest. */
  current: AppVersionView;
  summary: ManifestSummary | null;
  /** When the active version went live (its last status change) and who stored it. */
  installedAt: string | null;
  installedBy: string | null;
  /** The active version's menu entries, which exist as CRM routes while the app is active. */
  navLinks: AppNavLink[];
  versions: AppVersionView[];
}

function parseManifest(manifest: unknown): AppManifest | null {
  const parsed = AppManifestSchema.safeParse(manifest);
  return parsed.success ? parsed.data : null;
}

export function authorLabel(record: Pick<TenantAppRecord, 'created_by_name' | 'created_by_email'>): string {
  return record.created_by_name?.trim() || record.created_by_email?.trim() || 'Unknown';
}

/** The versions of one app, newest first, each with its changes against the version before it. */
export function buildVersionViews(records: readonly TenantAppRecord[]): AppVersionView[] {
  const ascending = [...records].sort((a, b) => a.version - b.version);
  const views: AppVersionView[] = [];
  let previous: AppManifest | null = null;
  ascending.forEach((record, index) => {
    const manifest = parseManifest(record.manifest);
    const changes = manifest && previous ? describeDiff(diffManifests(previous, manifest)) : [];
    views.push({
      id: record.id,
      version: record.version,
      status: record.status,
      manifest,
      createdAt: record.created_at,
      updatedAt: record.updated_at,
      createdBy: authorLabel(record),
      changes,
      isFirst: index === 0,
    });
    if (manifest) previous = manifest;
  });
  return views.reverse();
}

function appStatus(versions: readonly AppVersionView[]): TenantAppStatus {
  if (versions.some((version) => version.status === 'active')) return 'active';
  if (versions.some((version) => version.status === 'disabled')) return 'disabled';
  return 'draft';
}

/** Groups the rows by app key; apps sort by name, with active ones first. */
export function groupInstalledApps(records: readonly TenantAppRecord[]): InstalledAppView[] {
  const byKey = new Map<string, TenantAppRecord[]>();
  for (const record of records) {
    const list = byKey.get(record.key) ?? [];
    list.push(record);
    byKey.set(record.key, list);
  }
  const apps: InstalledAppView[] = [];
  for (const [key, rows] of byKey) {
    const versions = buildVersionViews(rows);
    const active = versions.find((version) => version.status === 'active') ?? null;
    const current = active ?? versions[0];
    if (!current) continue;
    const describing = current.manifest ?? versions.find((version) => version.manifest)?.manifest ?? null;
    const summary = describing ? summarizeManifest(describing) : null;
    apps.push({
      key,
      name: summary?.name ?? key,
      description: summary?.description,
      status: appStatus(versions),
      activeVersion: active,
      current,
      summary,
      installedAt: active?.updatedAt ?? null,
      installedBy: active?.createdBy ?? null,
      navLinks: active?.manifest ? active.manifest.nav.items.map((item) => ({ path: item.path, label: item.label })) : [],
      versions,
    });
  }
  return apps.sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || a.name.localeCompare(b.name));
}

export const STATUS_LABELS: Record<TenantAppStatus, string> = {
  active: 'Active',
  disabled: 'Disabled',
  draft: 'Draft',
};
